import { useEffect, useRef, type ReactNode } from "react";

export const SITE = "https://drhint.com";

/** Reports document height to the parent page so /embed.js can auto-resize. */
export function useAutoHeight() {
  const sent = useRef(0);
  useEffect(() => {
    const fid = new URLSearchParams(window.location.search).get("fid");
    const send = () => {
      const h = Math.ceil(document.documentElement.scrollHeight);
      if (Math.abs(h - sent.current) < 12) return;
      sent.current = h;
      window.parent?.postMessage({ type: "drhint:height", fid, height: h }, "*");
    };
    send();
    const ro = new ResizeObserver(send);
    ro.observe(document.body);
    const t = setInterval(send, 1500);
    return () => {
      ro.disconnect();
      clearInterval(t);
    };
  }, []);
}

export function EmbedStyles() {
  return (
    <style>{`
      html,body{margin:0;padding:0;background:#0d0e1a;color:#f3f4f8;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif}
      *,*::before,*::after{box-sizing:border-box}
      a{color:#e8c14a}
      .dh-wrap{max-width:820px;margin:0 auto;padding:16px}
      .dh-card{background:#15172a;border:1px solid #262a45;border-radius:14px;padding:14px;margin-bottom:14px}
      .dh-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px}
      .dh-k{font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#9aa0b8}
      .dh-v{font-size:20px;font-weight:800;color:#e8c14a}
      .dh-h1{font-size:22px;font-weight:900;margin:0 0 6px}
      .dh-h2{font-size:16px;font-weight:800;margin:0 0 10px}
      .dh-cta{display:block;text-align:center;background:linear-gradient(135deg,#e8c14a,#c99a1f);color:#12131f !important;font-weight:800;padding:11px;border-radius:10px;text-decoration:none;margin-top:10px}
      .dh-ghost{display:block;text-align:center;border:1px solid #3a3f66;color:#e8c14a !important;font-weight:700;padding:10px;border-radius:10px;text-decoration:none;margin-top:8px;font-size:14px}
      .dh-lock{opacity:.55;position:relative}
      input,select{width:100%;padding:9px 10px;border-radius:9px;border:1px solid #333857;background:#0f1122;color:#f3f4f8;font-size:14px}
      label{font-size:11px;color:#9aa0b8;display:block;margin-bottom:4px}
      table{width:100%;border-collapse:collapse;font-size:14px}
      th,td{padding:8px 6px;border-bottom:1px solid #252943;text-align:left}
      th{color:#9aa0b8;font-weight:600;font-size:12px}
      .dh-foot{font-size:11px;color:#8b90a8;text-align:center;padding:10px 0 18px}
      .dh-live{display:inline-flex;align-items:center;gap:6px;font-size:11px;color:#9aa0b8;text-transform:uppercase;letter-spacing:1px}
      .dh-dot{width:7px;height:7px;border-radius:99px;background:#ef4444;box-shadow:0 0 8px #ef4444;display:inline-block}
    `}</style>
  );
}

export function EmbedPage({ children, target }: { children: ReactNode; target: string }) {
  useAutoHeight();
  return (
    <>
      <EmbedStyles />
      <div className="dh-wrap">
        {children}
        <div className="dh-foot">
          Live data by{" "}
          <a href={`${SITE}${target}`} target="_blank" rel="noopener">
            DrHint.com
          </a>{" "}
          — updated every 10 minutes.
        </div>
      </div>
    </>
  );
}

export function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="dh-card" style={{ margin: 0 }}>
      <div className="dh-k">{k}</div>
      <div className="dh-v">{v}</div>
    </div>
  );
}

/** Inline SVG line chart (no deps, SSR safe). */
export function MiniChart({ points, label }: { points: number[]; label: string }) {
  if (points.length < 2) return null;
  const w = 800;
  const h = 180;
  const pad = 10;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = (w - pad * 2) / (points.length - 1);
  const coords = points.map((p, i) => `${(pad + i * step).toFixed(1)},${(h - pad - ((p - min) / range) * (h - pad * 2)).toFixed(1)}`);
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 180 }}>
      <polyline points={`${pad},${h - pad} ${coords.join(" ")} ${w - pad},${h - pad}`} fill="rgba(232,193,74,0.12)" stroke="none" />
      <polyline points={coords.join(" ")} fill="none" stroke="#e8c14a" strokeWidth={2.5} strokeLinejoin="round" />
    </svg>
  );
}
